/* Original Shopify implementation of observed editorial navigation patterns. */
(() => {
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const cleanups = new WeakMap();
  function init(root = document) {
    root.querySelectorAll('.xkk-footer-group').forEach(group => {
      if (group.dataset.accordionReady) return;
      group.dataset.accordionReady = 'true';
      const mobile = matchMedia('(max-width: 749px)');
      const sync = () => { group.open = !mobile.matches; };
      sync(); mobile.addEventListener('change', sync);
    });
    root.querySelectorAll('.xkk-scene-bag').forEach(bag => {
      if (bag.dataset.sceneReady) return;
      bag.dataset.sceneReady = 'true';
      bag.addEventListener('keydown', e => { if (e.key === 'Escape') { bag.open = false; bag.querySelector('summary').focus(); } });
      bag.addEventListener('focusout', e => { if (!bag.contains(e.relatedTarget)) bag.open = false; });
    });
    root.querySelectorAll('[data-xkk-cinema]').forEach(section => {
      if (section.dataset.cinemaReady) return;
      section.dataset.cinemaReady = 'true';
      const frames = [...section.querySelectorAll('[data-frame]')], dots = [...section.querySelectorAll('[data-scene]')];
      const controls = section.querySelector('.xkk-cinema__controls'), pause = section.querySelector('.xkk-motion-toggle');
      let index = 0, timer, paused = reduced.matches, visible = true, focus = false, startX = null;
      const interval = Math.max(4000, Number(section.dataset.interval) || 5000);
      const show = next => {
        index = (next + frames.length) % frames.length;
        frames.forEach((frame, i) => { frame.classList.toggle('is-active', i === index); frame.setAttribute('aria-hidden', String(i !== index)); });
        dots.forEach((dot, i) => { if (i === index) dot.setAttribute('aria-current', 'true'); else dot.removeAttribute('aria-current'); });
        section.dataset.activeScene = String(index);
      };
      const sync = () => {
        clearInterval(timer);
        const stopped = paused || !visible || document.hidden || focus || reduced.matches;
        section.classList.toggle('motion-paused', stopped);
        pause.setAttribute('aria-pressed', String(paused));
        pause.textContent = paused ? 'Play motion' : 'Pause motion';
        if (!stopped) timer = setInterval(() => show(index + 1), interval);
      };
      const manual = next => { paused = true; show(next); sync(); };
      controls.hidden = false; show(0);
      pause.addEventListener('click', () => { paused = !paused; sync(); });
      dots.forEach(dot => dot.addEventListener('click', () => manual(Number(dot.dataset.scene))));
      section.addEventListener('touchstart', e => { startX = e.touches[0].clientX; }, { passive: true });
      section.addEventListener('touchend', e => { if (startX !== null && Math.abs(e.changedTouches[0].clientX - startX) > 45) manual(index + (e.changedTouches[0].clientX < startX ? 1 : -1)); startX = null; }, { passive: true });
      section.addEventListener('focusin', () => { focus = true; sync(); });
      section.addEventListener('focusout', e => { focus = section.contains(e.relatedTarget); sync(); });
      const onVisibility = () => sync(), onReduced = () => { paused = reduced.matches; sync(); };
      document.addEventListener('visibilitychange', onVisibility); reduced.addEventListener('change', onReduced);
      const observer = new IntersectionObserver(entries => { visible = entries[0].isIntersecting; sync(); });
      observer.observe(section); sync();
      cleanups.set(section, () => { clearInterval(timer); observer.disconnect(); document.removeEventListener('visibilitychange', onVisibility); reduced.removeEventListener('change', onReduced); });
    });
    root.querySelectorAll('.xkk-mobile-menu').forEach(menu => {
      if (menu.dataset.drawerReady) return; menu.dataset.drawerReady = 'true';
      const trigger = menu.querySelector('summary'), panel = menu.querySelector('.xkk-mobile-menu__panel');
      const close = () => { menu.open = false; trigger.focus(); };
      menu.querySelector('[data-close-menu]')?.addEventListener('click', close);
      menu.querySelector('[data-menu-backdrop]')?.addEventListener('click', close);
      menu.addEventListener('toggle', () => { document.body.classList.toggle('xkk-menu-open', menu.open); if (menu.open) panel.querySelector('button').focus(); });
      menu.addEventListener('keydown', e => {
        if (e.key === 'Escape') { e.preventDefault(); close(); }
        if (e.key === 'Tab' && menu.open) {
          const els = [...panel.querySelectorAll('a,button,input,summary')].filter(el => el.getBoundingClientRect().width);
          if (e.shiftKey && document.activeElement === els[0]) { e.preventDefault(); els.at(-1).focus(); }
          else if (!e.shiftKey && document.activeElement === els.at(-1)) { e.preventDefault(); els[0].focus(); }
        }
      });
    });
    root.querySelectorAll('.xkk-mobile-search-link').forEach(link => {
      if(link.dataset.searchReady) return; link.dataset.searchReady = 'true';
      link.addEventListener('click', e => { const menu = document.querySelector('.xkk-mobile-menu'); if(!menu) return; e.preventDefault(); menu.open = true; requestAnimationFrame(() => requestAnimationFrame(() => menu.querySelector('input[type=search]')?.focus())); });
    });
    root.querySelectorAll('.xkk-search').forEach((form, i) => {
      if (form.dataset.suggestReady) return; form.dataset.suggestReady = 'true';
      const input = form.querySelector('input[type=search]');
      const box = document.createElement('div'); box.className = 'xkk-search-suggestions'; box.id = 'xkk-suggest-' + i; box.hidden = true;
      box.setAttribute('aria-label', 'Suggested products'); form.append(box);
      input.setAttribute('aria-controls', box.id); input.setAttribute('aria-expanded', 'false');
      let timeout, abort;
      const hide = () => { box.hidden = true; input.setAttribute('aria-expanded', 'false'); };
      input.addEventListener('input', () => {
        clearTimeout(timeout); abort?.abort(); hide(); if (input.value.trim().length < 2) return;
        timeout = setTimeout(async () => {
          abort = new AbortController();
          try {
            const base = (window.Shopify?.routes?.root || '/');
            const response = await fetch(base + 'search/suggest.json?q=' + encodeURIComponent(input.value.trim()) + '&resources[type]=product&resources[limit]=5', { signal: abort.signal });
            if (!response.ok) return;
            const products = (await response.json()).resources?.results?.products || []; box.replaceChildren();
            products.forEach(product => {
              const link = document.createElement('a'); link.href = product.url; link.textContent = product.title;
              if (product.image) { const img = document.createElement('img'); img.src = product.image; img.alt = ''; link.prepend(img); }
              box.append(link);
            });
            if (products.length) { box.hidden = false; input.setAttribute('aria-expanded', 'true'); }
          } catch (e) { if (e.name !== 'AbortError') hide(); }
        }, 220);
      });
      form.addEventListener('keydown', e => { if (e.key === 'Escape') hide(); if (e.key === 'ArrowDown' && !box.hidden && e.target === input) { e.preventDefault(); box.querySelector('a')?.focus(); } });
      form.addEventListener('focusout', e => { if (!form.contains(e.relatedTarget)) hide(); });
      document.addEventListener('click', e => { if (!form.contains(e.target)) hide(); });
    });
  }
  function recents() {
    let items = [];
    try {
      items = JSON.parse(localStorage.getItem('xkk-recent-products') || '[]');
      if (!Array.isArray(items)) items = [];
      items = items.filter(p => p && typeof p.url === 'string' && /^\/products\//.test(p.url) && typeof p.title === 'string');
      const source = document.querySelector('#xkk-viewed-product');
      if (source) { const product = JSON.parse(source.textContent); items = [product, ...items.filter(p => p.handle !== product.handle)].slice(0,8); localStorage.setItem('xkk-recent-products', JSON.stringify(items)); }
    } catch { items = []; }
    const build = target => {
      target.replaceChildren();
      items.forEach(product => { const a = document.createElement('a'); a.href = product.url; const img = document.createElement('img'); img.src = product.image; img.alt = ''; img.loading = 'lazy'; const title = document.createElement('span'); title.textContent = product.title.length > 50 ? product.title.slice(0,50) + '…' : product.title; a.append(img,title); target.append(a); });
    };
    const section = document.querySelector('[data-xkk-recent]'); if (section && items.length) { section.hidden = false; build(section.querySelector('.xkk-recent__items')); }
    const dialog = document.querySelector('.xkk-recent-dialog'); if (!dialog) return;
    if (items.length) build(dialog.querySelector('[data-recent-panel]'));
    let opener;
    document.querySelectorAll('[data-open-recents]').forEach(button => button.addEventListener('click', () => { opener = button; dialog.showModal(); }));
    dialog.querySelector('[data-close-recents]').addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', e => { if (e.target === dialog) { const r = dialog.getBoundingClientRect(); if(e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) dialog.close(); } });
    dialog.addEventListener('close', () => opener?.focus());
  }
  function ready() { init(); recents(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready); else ready();
  document.addEventListener('shopify:section:load', e => init(e.target));
  document.addEventListener('shopify:section:unload', e => e.target.querySelectorAll('[data-xkk-cinema]').forEach(n => cleanups.get(n)?.()));
})();
